/**
 * Client Groups service — taxonomy for tagging managed broker accounts.
 *
 * SuperAdmin: full CRUD on /admin/super/client-groups[/{group_id}]
 * Any authenticated user: read-only list of (id, name, color) via /client-groups
 *   for populating filter dropdowns. Phase 2 layers in per-broker-account
 *   assignment + delete-usage guard.
 */
import apiClient from './apiClient';
import { ApiResponse } from '../types';

const API_TYPE = 'api' as const;

export interface ClientGroup {
  group_id: string;
  name: string;
  description: string;
  color: string;
  created_by_user_id: string;
  created_at: string;
  updated_at: string;
}

/** Lightweight projection returned by GET /client-groups (filter dropdowns). */
export interface ClientGroupOption {
  group_id: string;
  name: string;
  color: string;
}

export interface CreateClientGroupRequest {
  name: string;
  description?: string;
  color?: string;
}

export interface UpdateClientGroupRequest {
  name?: string;
  description?: string;
  color?: string;
}

function unwrapErr(error: any, fallback: string): Error {
  return new Error(
    error?.response?.data?.message
    || error?.response?.data?.error
    || error?.message
    || fallback,
  );
}

class ClientGroupsService {
  /** SuperAdmin: full list with all fields. */
  async listAdmin(): Promise<ClientGroup[]> {
    try {
      const response = await apiClient.get<ApiResponse<ClientGroup[]>>(
        '/admin/super/client-groups', undefined, API_TYPE,
      );
      if (!response.success || !response.data) {
        throw new Error(response.message || 'Failed to list client groups');
      }
      return response.data;
    } catch (error: any) {
      throw unwrapErr(error, 'Failed to list client groups');
    }
  }

  /** Cognito-only: minimal (id+name+color) list for AM filter dropdowns. */
  async listForFilter(): Promise<ClientGroupOption[]> {
    try {
      const response = await apiClient.get<ApiResponse<ClientGroupOption[]>>(
        '/client-groups', undefined, API_TYPE,
      );
      if (!response.success || !response.data) {
        throw new Error(response.message || 'Failed to list client groups');
      }
      return response.data;
    } catch (error: any) {
      throw unwrapErr(error, 'Failed to list client groups');
    }
  }

  /** SuperAdmin: create. Returns the new row. */
  async create(req: CreateClientGroupRequest): Promise<ClientGroup> {
    try {
      const response = await apiClient.post<ApiResponse<ClientGroup>>(
        '/admin/super/client-groups', req, undefined, API_TYPE,
      );
      if (!response.success || !response.data) {
        throw new Error(response.message || 'Failed to create client group');
      }
      return response.data;
    } catch (error: any) {
      throw unwrapErr(error, 'Failed to create client group');
    }
  }

  /** SuperAdmin: rename / edit description / color. Returns the updated row. */
  async update(groupId: string, req: UpdateClientGroupRequest): Promise<ClientGroup> {
    try {
      const response = await apiClient.put<ApiResponse<ClientGroup>>(
        `/admin/super/client-groups/${encodeURIComponent(groupId)}`,
        req, undefined, API_TYPE,
      );
      if (!response.success || !response.data) {
        throw new Error(response.message || 'Failed to update client group');
      }
      return response.data;
    } catch (error: any) {
      throw unwrapErr(error, 'Failed to update client group');
    }
  }

  /** SuperAdmin: delete. Phase 2 layers in broker-accounts usage guard. */
  async delete(groupId: string): Promise<void> {
    try {
      const response = await apiClient.delete<ApiResponse<{ group_id: string }>>(
        `/admin/super/client-groups/${encodeURIComponent(groupId)}`,
        undefined, API_TYPE,
      );
      if (!response.success) {
        throw new Error(response.message || 'Failed to delete client group');
      }
    } catch (error: any) {
      throw unwrapErr(error, 'Failed to delete client group');
    }
  }
}

const clientGroupsService = new ClientGroupsService();
export default clientGroupsService;
export { ClientGroupsService };
